import { useState } from 'react'
import { useProject } from '@/contexts/project-context'
import { useUser, usePermissions } from '@/lib/hooks'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Plus, Building2, Loader2, MoreVertical, Pencil, Trash2, Settings } from 'lucide-react'
import { toast } from 'sonner'

export default function ProjectSelector() {
  const { 
    currentProject, 
    projects, 
    projectsLoading, 
    switchProject, 
    createProject, 
    updateProject,
    deleteProject,
    projectMembers 
  } = useProject()
  const { profile: user } = useUser()
  
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state for create
  const [projectName, setProjectName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [description, setDescription] = useState('')
  
  // Form state for edit
  const [editProjectName, setEditProjectName] = useState('')
  const [editCompanyName, setEditCompanyName] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // Use permissions hook for role-based UI
  const permissions = usePermissions()

  const handleProjectChange = (projectId: string) => {
    if (projectId === 'create-new') {
      setShowCreateDialog(true)
    } else {
      switchProject(projectId)
    }
  }

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      setError('Project name is required')
      return
    }

    try {
      setCreating(true)
      setError(null)
      
      const newProject = await createProject(
        projectName.trim(),
        description.trim() || undefined,
        companyName.trim() || undefined
      )
      
      // Switch to the new project
      await switchProject(newProject.id)
      
      // Reset form and close dialog
      setProjectName('')
      setCompanyName('')
      setDescription('')
      setShowCreateDialog(false)
      
      toast.success('Project created successfully')
    } catch (err: any) {
      setError(err.message || 'Failed to create project')
    } finally {
      setCreating(false)
    }
  }

  const handleEditClick = () => {
    if (!currentProject) return
    
    // Populate edit form with current values
    setEditProjectName(currentProject.name)
    setEditCompanyName(currentProject.company_name || '')
    setEditDescription(currentProject.description || '')
    setShowEditDialog(true)
  }

  const handleUpdateProject = async () => {
    if (!currentProject || !editProjectName.trim()) {
      setError('Project name is required')
      return
    }

    try {
      setUpdating(true)
      setError(null)
      
      await updateProject(currentProject.id, {
        name: editProjectName.trim(),
        company_name: editCompanyName.trim() || null,
        description: editDescription.trim() || null,
      })
      
      setShowEditDialog(false)
      toast.success('Project updated successfully')
    } catch (err: any) {
      setError(err.message || 'Failed to update project')
    } finally {
      setUpdating(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!currentProject) return

    try {
      setDeleting(true)
      await deleteProject(currentProject.id)
      setShowDeleteDialog(false)
      toast.success('Project deleted successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete project')
    } finally {
      setDeleting(false)
    }
  }

  if (projectsLoading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading projects...</span>
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center space-x-2">
        <Building2 className="h-5 w-5 text-gray-600" />
        <Select
          value={currentProject?.id || ''}
          onValueChange={handleProjectChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
            <SelectItem value="create-new">
              <div className="flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Create New Project
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        
        {currentProject && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => console.log('Project settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Project Settings
              </DropdownMenuItem>
              {permissions.canEditProject && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleEditClick}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Project
                  </DropdownMenuItem>
                </>
              )}
              {permissions.canDeleteProject && (
                <DropdownMenuItem 
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-red-600"
                  disabled={projects.length <= 1}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Project
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Create a new project to track equity for a different company or venture.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., My Startup"
                disabled={creating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Acme Inc."
                disabled={creating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the project..."
                rows={3}
                disabled={creating}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateProject} disabled={creating || !projectName.trim()}>
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>
              Update your project details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-project-name">Project Name *</Label>
              <Input
                id="edit-project-name"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                placeholder="e.g., My Startup"
                disabled={updating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Company Name</Label>
              <Input
                id="edit-company-name"
                value={editCompanyName}
                onChange={(e) => setEditCompanyName(e.target.value)}
                placeholder="e.g., Acme Inc."
                disabled={updating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description of the project..."
                rows={3}
                disabled={updating}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateProject} disabled={updating || !editProjectName.trim()}>
              {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{currentProject?.name}"? This action cannot be undone 
              and will permanently delete all associated data including tasks, contributions, and cap table entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}